// Libs
import classNames from 'classnames/bind';
import { Box, Button, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Views, ViewsProps } from 'react-big-calendar';
import moment from 'moment';
import { useSelector } from 'react-redux';
// Components, Layouts, Pages
import { BaseSplitButton, Loading } from '~/components/common';
import { Calendar, FormScheduleModal, HeaderCalendar } from '~/components/module/schedules';
// Others
import { StorageEnum } from '~/utils/enum/base';
import {
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  DEFAULT_LIMIT_1000,
  EMPTY_STRING,
  SCHEDULE_MODE_FULL,
} from '~/utils/constants/common';
import { DateFormatEnum, RoleEnum } from '~/utils/enum/common';
import { adminRouteAbsolute } from '~/utils/routes/admin';
import { staffRouteAbsolute } from '~/utils/routes/staff';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { CalendarViewMode } from '~/utils/type/common';
import { ISchedule } from '~/utils/interface/schedule';
import { estimateActions, selectEstimateDetail } from '~/thunks/estimate/estimateSlice';
import { getEstimateScheduleList } from '~/thunks/estimate/estimateThunk';
// Styles, images, icons
import styles from './EstimateSchedule.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const views: ViewsProps = {
  month: true,
  week: true,
  day: true,
};

const EstimateSchedule = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/estimate']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id: estimateId } = useParams();
  const currentRole: string = localStorage.getItem(StorageEnum.ROLE) ?? EMPTY_STRING;
  //#endregion Declare Hook

  //#region Selector
  const {
    scheduleEstimate,
    scheduleCurrentTime,
    isRefreshEstimateSchedule,
    calendarViewMode,
    scheduleMode,
    events,
  } = useAppSelector((state) => state.estimate);
  const estimateDetail = useSelector(selectEstimateDetail);
  //#endregion Selector

  //#region Declare State
  const [isShowFormScheduleModal, setIsShowFormScheduleModal] = useState<boolean>(false);
  const [scheduleId, setScheduleId] = useState<string>(EMPTY_STRING);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  const timeValue = useMemo(() => {
    switch (calendarViewMode) {
      case Views.WEEK:
        const startOfWeek = moment(scheduleCurrentTime).startOf('isoWeek').format('MMMM DD');
        const endOfWeek = moment(scheduleCurrentTime).endOf('isoWeek').format('MMMM DD');
        return `${startOfWeek} - ${endOfWeek}`;
      case Views.DAY:
        return moment(scheduleCurrentTime).format('dddd, MMMM DD');
      default:
        return moment(scheduleCurrentTime).format('MMMM YYYY');
    }
  }, [calendarViewMode, scheduleCurrentTime]);
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetScheduleEstimate();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleCurrentTime, calendarViewMode]);

  useEffect(() => {
    if (!isRefreshEstimateSchedule) return;

    handleGetScheduleEstimate();
    dispatch(estimateActions.setIsRefreshScheduleData(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshEstimateSchedule]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleShowFormSchedule = () => {
    setIsShowFormScheduleModal(true);
  };

  const handleShowAddJob = () => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(adminRouteAbsolute.createJob);
        break;

      case RoleEnum.STAFF:
        navigate(staffRouteAbsolute.createJob);
        break;
    }
  };

  const handleShowAddEstimate = () => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(adminRouteAbsolute.createEstimate);
        break;

      case RoleEnum.STAFF:
        navigate(staffRouteAbsolute.createEstimate);
        break;
    }
  };

  const handlePreviousTime = () => {
    dispatch(
      estimateActions.setScheduleCurrentTime(
        moment(scheduleCurrentTime).subtract(1, calendarViewMode).format(DateFormatEnum.YYYY_MM_DD)
      )
    );
  };

  const handleNextTime = () => {
    dispatch(
      estimateActions.setScheduleCurrentTime(
        moment(scheduleCurrentTime).add(1, calendarViewMode).format(DateFormatEnum.YYYY_MM_DD)
      )
    );
  };

  const handleToday = () => {
    dispatch(estimateActions.setScheduleCurrentTime(moment().format(DateFormatEnum.YYYY_MM_DD)));
  };

  const handleChangeCalendarView = (viewMode: CalendarViewMode) => {
    dispatch(estimateActions.changeCalendarViewMode(viewMode));
  };

  const onSelectEvent = (event: ISchedule) => {
    setScheduleId(event.id);
    setIsShowFormScheduleModal(true);
  };

  const handleCloseFormScheduleModal = () => {
    setIsShowFormScheduleModal(false);
    setScheduleId(EMPTY_STRING);
  };

  const handleGetScheduleEstimate = () => {
    dispatch(estimateActions.changeEstimateSchedule([]));
    setIsLoading(true);

    const startDate = moment(scheduleCurrentTime).startOf(calendarViewMode).format(DateFormatEnum.YYYY_MM_DD);
    const endDate = moment(scheduleCurrentTime).endOf(calendarViewMode).format(DateFormatEnum.YYYY_MM_DD);
    const assigneeIds = estimateDetail?.assignees
      ? estimateDetail.assignees.map((assignee) => assignee.id)
      : [];
    dispatch(
      getEstimateScheduleList({
        page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
        limit: DEFAULT_LIMIT_1000,
        businessId: estimateId,
        type: SCHEDULE_MODE_FULL.toUpperCase(),
        // businessType: [ScheduleTypeEnum.ESTIMATE],
        startDate,
        endDate,
        view: calendarViewMode.toUpperCase(),
        employeeIds: assigneeIds,
      })
    )
      .unwrap()
      .then((res) => {})
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };
  //#endregion Handle Function

  return (
    <Box component={'div'} id='estimateSchedulePage' className={cx('container')}>
      {/* <BaseToolbar textAction={t('schedule_button_add_schedule')} onAction={handleShowFormSchedule} /> */}

      <Stack
        direction={'row'}
        alignItems={'center'}
        justifyContent={'flex-end'}
        className={cx('actionsGroup')}
      >
        <BaseSplitButton label={t('schedule_button_add_schedule')} onClick={handleShowFormSchedule}>
          <Button variant='text' fullWidth onClick={handleShowAddJob}>
            {t('schedule_button_add_job')}
          </Button>

          <Button variant='text' fullWidth onClick={handleShowAddEstimate}>
            {t('schedule_button_add_estimate')}
          </Button>
        </BaseSplitButton>
      </Stack>

      <HeaderCalendar
        views={views}
        timeValue={timeValue}
        calendarViewMode={calendarViewMode}
        onPreviousTime={handlePreviousTime}
        onNextTime={handleNextTime}
        onToday={handleToday}
        onChangeCalendarView={handleChangeCalendarView}
        scheduleMode={scheduleMode}
      />

      <Box className={cx('calendar')}>
        <Calendar
          type='estimate'
          view={calendarViewMode}
          date={scheduleCurrentTime}
          events={events || []}
          timeLineEvents={scheduleEstimate}
          onSelectEvent={onSelectEvent}
          scheduleMode={scheduleMode}
        />
      </Box>

      {isShowFormScheduleModal && (
        <FormScheduleModal
          onClose={handleCloseFormScheduleModal}
          scheduleId={scheduleId}
          estimateId={estimateId}
        />
      )}

      {isLoading && <Loading />}
    </Box>
  );
};

export default EstimateSchedule;
